import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { FormModal, RHFTextField } from '../components/form';
import Typography from '@mui/material/Typography';
import { Container, Table, tableCellClasses, Button, Box, IconButton, Grid, Select, MenuItem, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchSalesCoordinatorRemovalTransfers, updateSalesCoordinatorRemovalTransfer, deleteSalesCoordinatorRemovalTransfer, fetchCustomers, fetchVehicles, fetchSolutions, fetchTechnicalRemovalTransfer, emailTechnicalRemovalTransferPdf } from "../services/ApiService";
import BulkUploadAttachment from '../components/BulkUploadAttachment';
import EditIcon from '@mui/icons-material/Edit';
import GenerateInvoiceButton from '../components/Invoice';
import DataTable from '../components/DataTable';

const editRecordSchema = z.object({
  cost: z.string().optional(),
  billing_date: z.string().min(1, "Required"),
  discount: z.string().optional(),
  discounted_cost: z.string().optional(),
  remarks: z.string().optional(),
});

const BillingCalculator = () => {
  const { watch, setValue } = useFormContext();
  const cost = watch("cost");
  const discount = watch("discount");
  React.useEffect(() => {
    const c = parseFloat(cost || 0);
    const d = parseFloat(discount || 0);
    setValue("discounted_cost", isNaN(c - c * d / 100) ? "" : String(Math.round(c - c * d / 100)));
  }, [cost, discount, setValue]);
  return null;
};

export function SalesCoordinatorRemovalTransfer() {

  const [devicesOptions, setDevicesOptions] = React.useState([])
  const [selectedCustomerSupport, setSelectedCustomerSupport] = React.useState(null);
  const [csModalOpen, setCSModalOpen] = React.useState(false);
  const [haulier, setHauliers] = React.useState([])
  const [devices, setDevices] = React.useState([])
  const [trucks, setTrucks] = React.useState([])
  const [employees, setEmployees] = React.useState([])
  const [editModalOn, setEditModalOn] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [haulierOptions, setHaulierOptions] = React.useState([]);
  const [truckOptions, setTruckOptions] = React.useState([]);
  const [documentFile, setDocumentFile] = React.useState(null);

  React.useEffect(() => {
    fetchHauliers();
    fetchDevices()
    fetchTruck();
    fetchDeliveries();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewCustomerSupportDetail = async (saleId) => {
    try {
      const response = await fetchTechnicalRemovalTransfer(saleId);
      console.log(response.data);
      setSelectedCustomerSupport(response.data);
      setCSModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch sale detail:", error);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const response = await fetchSalesCoordinatorRemovalTransfers();
      console.log(response.data);
      setEmployees(response.data);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetchCustomers();
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.client_name;
        return acc;
      }, {});
      const haulier = response.data.reduce((acc, item) => {
        acc[item.id] = item.id;
        return acc;
      }, {});
      setHaulierOptions(haulierDict);
      setHauliers(haulier);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
      console.error("Error fetching hauliers:", error);
    }
  };

  const handleSendEmail = async (saleId) => {
    try {
      await emailTechnicalRemovalTransferPdf(saleId, {});
      alert("Email sent successfully!");
    } catch (error) {
      console.error("Email error:", error);
      alert("Failed to send email.");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStatusChange = async (saleId, newStatus, sale) => {
    const updatedSale = { ...sale, status: newStatus };
    try {
      await updateSalesCoordinatorRemovalTransfer(saleId, updatedSale);
      setEmployees((prevData) =>
        prevData.map((item) =>
          item.id === saleId ? { ...item, status: newStatus } : item
        )
      );
      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const fetchTruck = async () => {
    try {
      const response = await fetchVehicles();
      console.log(response.data);
      const allNames = response.data.reduce((acc, item) => {
        acc[item.id] = item.registration_number;
        return acc;
      }, {});
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setTruckOptions(haulierDict)
      setTrucks(allNames);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleEditOn = (row) => {
    setEditModalOn(true)
    setSelectedRow(row)
  }

  const handleEditOff = () => { setEditModalOn(false); setDocumentFile(null); }

  const fetchDevices = async () => {
    try {
      const response = await fetchSolutions();
      const allNames = response.data.map((employee) => employee.name);
      const deviceDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      const device = response.data.reduce((acc, item) => {
        acc[item.id] = item.transfer_cost;
        return acc;
      }, {});
      setDevicesOptions(deviceDict)
      setDevices(device);
    } catch (error) {
      const errorMessage = error.response?.statusText || error.message || 'Error fetching hauliers';
    }
  };

  const handleExport = () => {
    const doc = new jsPDF('l', 'mm', 'a3');
    doc.setFontSize(18);
    doc.text('Collection Recovery Removal Transfer Report', 14, 16);
    const columns = [
      'Attachment #', 'Technical Removal Transfer #', 'Discount',
      'Discounted Cost', 'Billing Date', 'Remarks', 'Status',
    ];
    const tableData = employees.map(row => [
      row.id, row.technical_removal_transfer || '', row.discount || '',
      row.discounted_cost || '', row.billing_date || '', row.remarks || '', row.status || '',
    ]);
    doc.autoTable({
      head: [columns], body: tableData, startY: 30,
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 25 }, 1: { cellWidth: 50 }, 2: { cellWidth: 25 },
        3: { cellWidth: 35 }, 4: { cellWidth: 40 }, 5: { cellWidth: 60 }, 6: { cellWidth: 30 },
      },
      theme: 'grid',
      didDrawPage: () => { doc.setFontSize(18); doc.text('Collection Recovery Removal Transfer Report', 14, 16); },
    });
    doc.save('Collection_Recovery_Removal_Transfer_Report.pdf');
  };

  const EditEmployeeModal = ({ selectedRow, open, handleClose }) => {
    return (
      <FormModal
        open={open}
        onClose={handleClose}
        title="Edit Record"
        schema={editRecordSchema}
        defaultValues={{
          cost: String(selectedRow?.transfer_cost ?? ""),
          billing_date: selectedRow?.billing_date ? selectedRow.billing_date.slice(0, 16) : "",
          discount: String(selectedRow?.discount ?? ""),
          discounted_cost: String(selectedRow?.discounted_cost ?? ""),
          remarks: selectedRow?.remarks ?? "",
        }}
        onSubmit={async (data) => {
          const fd = new FormData();
          Object.entries(data).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") fd.append(k, v); });
          if (documentFile) fd.append("document", documentFile);
          try {
            await updateSalesCoordinatorRemovalTransfer(selectedRow.id, fd);
            showSnackbar("Updated successfully", "success");
            fetchDeliveries();
          } catch {
            showSnackbar("Failed to update", "error");
            throw new Error();
          }
        }}
        submitLabel="Update"
        width={700}
      >
        {({ control }) => (
          <Grid container spacing={2.5}>
            <BillingCalculator />
            <Grid item xs={12} md={6}><RHFTextField control={control} name="cost" label="Cost" disabled /></Grid>
            <Grid item xs={12} md={6}><RHFTextField control={control} name="billing_date" label="Billing Date" type="datetime-local" InputLabelProps={{ shrink: true }} required /></Grid>
            <Grid item xs={12} md={6}><RHFTextField control={control} name="discount" label="Discount (%)" type="number" /></Grid>
            <Grid item xs={12} md={6}><RHFTextField control={control} name="discounted_cost" label="Discounted Cost" disabled /></Grid>
            <Grid item xs={12}><RHFTextField control={control} name="remarks" label="Remarks" multiline rows={3} /></Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>Document (optional)</Typography>
              <input type="file" accept=".pdf,.doc,.docx,.txt,image/*" style={{ width: "100%" }} onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)} />
            </Grid>
          </Grid>
        )}
      </FormModal>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ m: 2 }}>
      {editModalOn && selectedRow && (
        <EditEmployeeModal
          selectedRow={selectedRow}
          open={editModalOn}
          handleClose={handleEditOff}
        />
      )}
      <DataTable
        title="Collection Recovery Removal/Transfer List"
        columns={[
          { key: 'id', label: 'Attachment #' },
          { key: 'technical_removal_transfer', label: 'Technical R/T #', render: (row) => (
            <span onClick={() => handleViewCustomerSupportDetail(row.technical_removal_transfer)} style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{row.technical_removal_transfer}</span>
          )},
          { key: 'discount', label: 'Discount' },
          { key: 'discounted_cost', label: 'Discounted Cost' },
          { key: 'billing_date', label: 'Billing Date' },
          { key: 'remarks', label: 'Remarks' },
        ]}
        rows={employees}
        toolbarActions={<Button variant="contained" color="primary" style={{ marginLeft: "10px" }} onClick={handleExport}>Export to PDF</Button>}
        renderActions={(row) => (
          <Box display="flex" gap="6px">
            <IconButton color="secondary" size="small" onClick={() => handleEditOn(row)} sx={{ padding: '6px' }}><EditIcon /></IconButton>
            <Select fullWidth value={row.status} onChange={(e) => handleStatusChange(row.id, e.target.value, row)} size="small" sx={{ ml: 1, minWidth: 100 }}>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Invoice Sent">Invoice Sent</MenuItem>
            </Select>
            <GenerateInvoiceButton saleId={row.id} type='removal' />
          </Box>
        )}
      />
      <Dialog open={csModalOpen} onClose={() => setCSModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Customer Support Details</DialogTitle>
        <DialogContent>
          {selectedCustomerSupport ? (
            <Box>
              <Typography variant="body1"><strong>Location:</strong> {selectedCustomerSupport.location}</Typography>
              <Typography variant="body1"><strong>Resource Name:</strong> {selectedCustomerSupport.resource_name}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCSModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
